import { useCallback, useEffect, useRef } from 'react';
import {
  useHasQuizResults,
  useQuizGameState,
  useQuizMetaContext,
} from '@mentimeter/quiz';
import {
  GameStateEnums,
  type InitialGameStates,
  type QuizResult,
} from '@mentimeter/quiz-types';
import { addBreadcrumb } from '@mentimeter/errors/sentry';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import type { QuizSlideState } from './slide-state';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';
import type { QuizChoicesPresentationProps } from './types';

interface LivePresentationProps extends QuizChoicesPresentationProps {
  activeSession: number;
  imageUrl: string | undefined;
  timeBasedScoring: boolean;
  countdownToVote: number;
  initialGameState: InitialGameStates;
  shouldShowLegacyImage: boolean;
  data: QuizResult[];
  isPreviewResults: boolean;
}

export const LivePresentation = ({
  activeSession,
  initialGameState,
  countdownToVote,
  title,
  styledTitle,
  label,
  description,
  choices,
  textSizeOffset,
  imageUrl,
  timeBasedScoring,
  shouldShowLegacyImage,
  data,
  isPreviewResults,
}: LivePresentationProps) => {
  const { useSlideState } = usePresentationContext();
  const { totalCount, currentIndex } = useQuizMetaContext();

  const [{ started = false, starting = false }, setSlideState] =
    useSlideState<QuizSlideState>();
  const quizHasResults = useHasQuizResults();
  const {
    gameState,
    connectedPlayers,
    respondents,
    startQuiz: _startQuiz,
    createQuiz,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
    timeOffset = 0,
  } = useQuizGameState();

  const startQuiz = useCallback(async () => {
    await _startQuiz({ countdownToVote });
    setSlideState({ started: true, starting: false });
  }, [countdownToVote, setSlideState, _startQuiz]);

  const startedRef = useRef(false);

  /**
   * If the presenter were to navigate to the quiz slide again (or reset results), we need to either:
   * - Set `started` to `true` if there are results, so that the presenter can navigate to the next slide
   *  without having to start the quiz again.
   * - Reset `started` to `false` if there are no results, so that the presenter can start the quiz
   */
  useEffect(() => {
    if (quizHasResults) {
      setSlideState({ started: true, starting: false });
    } else {
      setSlideState({ started: false, starting: false });
    }
  }, [setSlideState, quizHasResults]);

  useEffect(() => {
    async function start() {
      addBreadcrumb({
        message: 'Starting quiz-choice',
        level: 'info',
      });
      startedRef.current = true;
      startQuiz();
    }

    if (starting && !started && !startedRef.current) {
      start();
    }
  }, [startQuiz, started, starting]);

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      addBreadcrumb({
        message: 'Creating quiz-choice',
        level: 'info',
        data: { gameState, activeSession, prevActiveSession },
      });
      createQuiz(sessionChanged ? GameStateEnums.LOBBY : initialGameState);
      createdRef.current = true;
      prevActiveSession.current = activeSession;
    }
  }, [createQuiz, initialGameState, activeSession, gameState]);

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          description={description}
          fontSizeOffset={textSizeOffset}
        />
        <CenteringContainer mt={4}>
          <QuizBarsDataHandler
            showCorrect={data.length > 0}
            rawResults={data}
            choices={choices}
            enableAnimations
          />
        </CenteringContainer>
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalCount}
            current={currentIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={timeBasedScoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            description={description}
            fontSizeOffset={textSizeOffset}
          />
          <CenteringContainer mt={4}>
            <Countdown
              type="choices"
              respondents={respondents.length}
              everyoneHasVoted={everyoneHasVoted}
              imageUrl={imageUrl}
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            >
              <QuizBarsDataHandler
                showCorrect={false}
                choices={choices}
                enableAnimations={false}
                rawResults={[]}
              />
            </Countdown>
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            description={description}
            fontSizeOffset={textSizeOffset}
          />
          <CenteringContainer mt={4}>
            <QuizBarsDataHandler
              showCorrect={data.length > 0}
              rawResults={data}
              choices={choices}
              enableAnimations
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading
            onReload={() => {
              if (connected) {
                createQuiz(initialGameState);
              } else {
                window.location.reload();
              }
            }}
          />
        </CenteringContainer>
      );
    }
  }
};
